import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { Size } from "@repo/ui/helpers/helpers";
import { Label } from "@repo/ui/Label/label";
import { IconBook2 } from "@tabler/icons-react";
import type { FC } from "react";
import { OutcomeSettingsForm } from "../../../components/recordingAssessmentEditor/outcomeSettingsForm.js";
import { SummaryTemplateSettingsForm } from "../../../components/recordingAssessmentEditor/summaryTemplateSettingsForm.js";
import { useIsFeatureFlagEnabled } from "../../../helpers/featureFlags/hooks.js";
import { AgentPhoneNumberSection } from "../agentPhoneNumberSection.js";
import { KnowledgeBaseSection } from "../components/knowledgeBaseSection.js";
import { ToolsSection } from "../components/toolsSection.js";
import { TeammateAgentDataExtractionFieldsSection } from "../teammateAgentDataExtractionFieldsSection.js";
import { InboundCallsSection } from "./inboundCallsSection.js";
import { OutboundCallsContainer } from "./outboundCallsContainer.js";
import { useStaticAgentConfig } from "./staticAgentConfigContext.js";

/**
 * The Configuration tab for a static agent. Composes the telephony settings —
 * the agent's number, inbound handling and outbound dialing — with the existing
 * controlled config blocks: Tools (agent config), Knowledge base + articles,
 * Outcome and Summary template (playbook settings, lifted out of the playbook
 * tree editor), and data-extraction fields. All are wired to the shared
 * controller's draft/publish flow.
 */
export const ResourcesAndOutputsTab: FC = () => {
  const {
    playbookId,
    phoneNumber,
    agentConfig,
    playbook,
    allKnowledgeBases,
    isActionInProgress,
    onPublish,
    setEditingAttachment,
    handleDataExtractionFieldsChange,
  } = useStaticAgentConfig();

  const isOutboundVoiceAgentsEnabled = useIsFeatureFlagEnabled(
    FeatureFlagKey.OutboundVoiceAgents,
  );

  const settings = agentConfig.data?.settings;

  const outcome = playbook.data?.outcome ?? null;

  return (
    // `relative`: react-aria's hidden inputs must count toward this pane's
    // overflow, not the page shell's.
    <div className="relative h-full overflow-y-auto bg-white p-6">
      <div className="mx-auto max-w-3xl space-y-8">
        <AgentPhoneNumberSection phoneNumber={phoneNumber} />

        {isOutboundVoiceAgentsEnabled && settings ? (
          <>
            <InboundCallsSection
              inbound={settings.inbound ?? { mode: "agent" }}
              forwardToError={agentConfig.validationErrors.inboundForwardTo}
              isDisabled={isActionInProgress}
              onChange={(inbound) =>
                agentConfig.onChange({ settings: { ...settings, inbound } })
              }
            />
            <OutboundCallsContainer />
          </>
        ) : null}

        <section
          aria-labelledby="resources-and-tools-heading"
          className="border-t border-border-light pt-8"
        >
          <h2
            id="resources-and-tools-heading"
            className="text-base font-semibold text-foreground"
          >
            Resources &amp; tools
          </h2>

          <div className="mt-6 space-y-8">
            <ToolsSection
              toolIds={agentConfig.data?.toolIds ?? []}
              hasUnpublishedChanges={
                agentConfig.unpublishedChanges.toolIdsChanged
              }
              onToolIdsChange={(newToolIds) =>
                agentConfig.onChange({ toolIds: newToolIds })
              }
              onPublish={onPublish}
              isDisabled={isActionInProgress}
            />

            <div>
              <Label size={Size.SMALL} className="mb-2">
                <span className="flex items-center gap-1.5">
                  Knowledge base
                  {playbook.unpublishedChanges.knowledgeBasesChanged ? (
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                  ) : null}
                </span>
              </Label>
              <KnowledgeBaseSection
                knowledgeBaseIds={playbook.data?.knowledgeBaseIds ?? []}
                allKnowledgeBases={allKnowledgeBases ?? []}
                onKnowledgeBaseIdsChange={(ids) =>
                  playbook.onChange({ knowledgeBaseIds: ids })
                }
                onEditAttachment={(knowledgeBaseId, attachmentId) =>
                  setEditingAttachment({ knowledgeBaseId, attachmentId })
                }
                banner={
                  <div className="rounded-lg bg-gray-50 p-4">
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 shrink-0">
                        <IconBook2 className="h-5 w-5 text-gray-400" />
                      </div>
                      <div className="text-sm text-gray-600">
                        <p>
                          Give your agent reference documents and information to
                          draw from during conversations, like FAQs, product
                          information, or company policies.
                        </p>
                      </div>
                    </div>
                  </div>
                }
              />
            </div>
          </div>
        </section>

        <section
          aria-labelledby="outputs-heading"
          className="border-t border-border-light pt-8"
        >
          <h2
            id="outputs-heading"
            className="text-base font-semibold text-foreground"
          >
            Outputs
          </h2>

          <div className="mt-6 space-y-8">
            {playbookId ? (
              <TeammateAgentDataExtractionFieldsSection
                playbookId={playbookId}
                fieldIds={playbook.data?.gradingDataExtractionFieldIds ?? []}
                onChange={handleDataExtractionFieldsChange}
                isDisabled={isActionInProgress}
                hasUnpublishedChanges={
                  playbook.unpublishedChanges.dataExtractionChanged
                }
              />
            ) : null}

            <div>
              <OutcomeSettingsForm
                outcome={outcome}
                initialOutcome={playbook.initialData?.outcome ?? null}
                liveOutcome={playbook.liveData?.outcome ?? null}
                onUpdateOutcome={(outcome) => playbook.onChange({ outcome })}
                isReadOnly={false}
                playbookId={playbookId}
                hasUnpublishedChanges={
                  playbook.unpublishedChanges.outcomeChanged
                }
                isTranscriptOnly
                // This tab flows inside the page's scroll region, so the form
                // grows with its content instead of scrolling internally.
                fillsHeight={false}
              />
            </div>

            <SummaryTemplateSettingsForm
              body={playbook.data?.summaryTemplateBody ?? null}
              liveBody={
                playbook.liveData
                  ? (playbook.liveData.summaryTemplateBody ?? null)
                  : undefined
              }
              onUpdateBody={(summaryTemplateBody) =>
                playbook.onChange({ summaryTemplateBody })
              }
              isReadOnly={false}
              hasUnpublishedChanges={
                playbook.unpublishedChanges.summaryTemplateChanged
              }
            />
          </div>
        </section>
      </div>
    </div>
  );
};
